import { JSX, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';

import {
  getRouteForbidden,
  getRouteLogin,
} from '@/shared/const/router.const.ts';
import { useAuth } from '@/shared/hooks/useAuth/useAuth';
import { usePermissions } from '@/shared/hooks/usePermissions/usePermissions';

type RequireAuthProps = {
  children: JSX.Element;
  permissions?: string[];
};

export function RequireAuth({ children, permissions }: RequireAuthProps) {
  const { isAuthenticated, profile } = useAuth();
  const location = useLocation();
  const currentUserRole = profile?.role.name;
  const { can } = usePermissions();

  const hasRequiredRoles = useMemo(() => {
    if (!permissions || !currentUserRole) {
      return true;
    }

    return permissions.some((perm) => can(perm));
  }, [permissions, currentUserRole, can]);

  if (!isAuthenticated) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to={getRouteLogin()} state={{ from: location }} replace />;
  }

  if (!hasRequiredRoles) {
    return (
      <Navigate to={getRouteForbidden()} state={{ from: location }} replace />
    );
  }

  return children;
}
